/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { Writable } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type {
  InitialData as DetailedJobAttachmentsFormInitialData,
  ValidData as DetailedJobAttachmentsFormValidData,
} from '@/components/forms/DetailedJobAttachmentsForm.vue';
import type { DataItem as DetailedJobAirRoadJobsTableDataItem } from '@/components/tables/DetailedJobAirRoadJobsTable.vue';
import type { DataItem as DetailedJobProductsTableDataItem } from '@/components/tables/DetailedJobProductsTable.vue';
import type { DataItem as DetailedJobQueriesTableDataItem } from '@/components/tables/DetailedJobQueriesTable.vue';
import type { DataItem as DetailedJobTicketsTableDataItem } from '@/components/tables/DetailedJobTicketsTable.vue';
import type { DetailedJobPageViewDataComposable } from '@/composables/data/useDetailedJobPageViewData';
import type { CommonRouteQueryComposable } from '@/composables/routeQueries/useCommonRouteQuery';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, reactive, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { useAbility } from '@casl/vue';
import _cloneDeep from 'lodash/cloneDeep';

import {
  fetchGetJobPrintoutFile,
  fetchPostJobAttachment,
} from '@/helpers/fetchers';
import * as detailedJobAirRoadJobsTableHelpers from '@/helpers/tables/detailedJobAirRoadJobs';
import * as detailedJobProductsTableHelpers from '@/helpers/tables/detailedJobProducts';
import * as detailedJobQueriesTableHelpers from '@/helpers/tables/detailedJobQueries';
import * as detailedJobTicketsTableHelpers from '@/helpers/tables/detailedJobTickets';
import * as routeNames from '@/constants/routeNames';

const storeRoutes = [
  routeNames.DETAILED_PARAM_JOB,
  routeNames.DETAILED_QUERY_JOB,
];

interface DefPrivateDetailedJobPageViewState {
  data: RequiredKeys<
    Partial<Writable<UnwrapRef<DetailedJobPageViewDataComposable>>>
  >;
  query: RequiredKeys<{
    jobId: number | undefined;
    jobDate: string | undefined;
  }>;
}

interface DefDetailedJobPageViewState {
  productsTableShowMoreLimit: number;
  productsTableShowMore: Record<string, boolean>;
  isFetchingFormData: boolean;
  isDownloading: boolean;
}

const defPrivateDetailedJobPageViewState: DefPrivateDetailedJobPageViewState = {
  data: {
    isFetchingDetailedJob: undefined,
    isFetchingDetailedCustomer: undefined,
    isFetchingShortZammadTickets: undefined,
    isFetchingAirroadJobs: undefined,
    isFetchingStopImages: undefined,
    isFetchingChannelsEmails: undefined,
    isFetchingStopLocations: undefined,

    detailedJob: undefined,
    detailedCustomer: undefined,
    shortZammadTickets: undefined,
    airroadJobs: undefined,
    stopImages: undefined,
    channelsEmails: undefined,
    stopLocations: undefined,

    detailedJobError: undefined,
    detailedCustomerError: undefined,
    shortZammadTicketsError: undefined,
    airroadJobsError: undefined,
    stopImagesError: undefined,
    channelsEmailsError: undefined,
    stopLocationsError: undefined,

    refreshDetailedJob: undefined,
    refreshDetailedCustomer: undefined,
    refreshShortZammadTickets: undefined,
    refreshAirroadJobs: undefined,
    refreshStopImages: undefined,
    refreshChannelsEmails: undefined,
    refreshStopLocations: undefined,

    reload: undefined,
  },
  query: {
    jobId: undefined,
    jobDate: undefined,
  },
};

const defDetailedJobPageViewState: DefDetailedJobPageViewState = {
  productsTableShowMoreLimit: 3,
  productsTableShowMore: {},
  isFetchingFormData: false,
  isDownloading: false,
};

function privateDetailedJobPageViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defPrivateDetailedJobPageViewState));
  const route = useRoute();

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    const query = state.query;
    Object.assign(state, _cloneDeep(defPrivateDetailedJobPageViewState));
    if (storeRoutes.includes(route.name as string)) {
      state.query = query; // preserve query while on the page
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  function setData(viewData: DetailedJobPageViewDataComposable) {
    state.data = reactive(viewData);
  }

  function setQuery(routeQuery: CommonRouteQueryComposable) {
    state.query = reactive({
      jobId: routeQuery.queryJobId,
      jobDate: routeQuery.queryJobDate,
    });
  }

  watch(
    () => route.name,
    function () {
      if (!storeRoutes.includes(route.name as string)) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    $reset,
    setData,
    setQuery,
  };
}

function detailedJobPageViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defDetailedJobPageViewState));

  const _detailedJobPageViewStore = usePrivateDetailedJobPageViewStore();

  const data = computed(function () {
    return _detailedJobPageViewStore.data;
  });

  const query = computed(function () {
    return _detailedJobPageViewStore.query;
  });

  const isFetchingAllData = computed(function () {
    return (
      data.value.isFetchingDetailedJob ||
      data.value.isFetchingDetailedCustomer ||
      data.value.isFetchingShortZammadTickets ||
      data.value.isFetchingAirroadJobs ||
      data.value.isFetchingStopImages ||
      data.value.isFetchingChannelsEmails ||
      data.value.isFetchingStopLocations
    );
  });

  const tablesDataLengths = computed(() => {
    const _tablesDataLengths: Record<string, number> = {};
    if (!data.value.detailedJob?.stops.length) return _tablesDataLengths;
    for (const stop of data.value.detailedJob.stops) {
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (stop.pickup_products && Object.keys(stop.pickup_products).length) {
        const tableDataLength = Object.keys(stop.pickup_products).length;
        _tablesDataLengths[stop.stop_number] = tableDataLength;
      }
    }
    return _tablesDataLengths;
  });

  const productsTablesData = computed(() => {
    const _productsTablesData: Record<
      string,
      DetailedJobProductsTableDataItem[]
    > = {};
    if (!data.value.detailedJob?.stops.length) return _productsTablesData;
    for (const stop of data.value.detailedJob.stops) {
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (stop.pickup_products && Object.keys(stop.pickup_products).length) {
        const detailedJobProductsTableData = [];
        for (const productId in stop.pickup_products) {
          const product = stop.pickup_products[productId];
          detailedJobProductsTableData.push(
            detailedJobProductsTableHelpers.getTableItem(product),
          );
        }
        if (state.productsTableShowMore[stop.stop_number]) {
          _productsTablesData[stop.stop_number] = detailedJobProductsTableData;
        } else {
          _productsTablesData[stop.stop_number] =
            detailedJobProductsTableData.slice(
              0,
              state.productsTableShowMoreLimit,
            );
        }
      }
    }
    return _productsTablesData;
  });

  const detailedJobTicketsTableData = computed(() => {
    const _detailedJobTicketsTableData: DetailedJobTicketsTableDataItem[] = [];
    if (!data.value.shortZammadTickets?.length) {
      return _detailedJobTicketsTableData;
    }
    for (const shortZammadTicket of data.value.shortZammadTickets) {
      _detailedJobTicketsTableData.push(
        detailedJobTicketsTableHelpers.getTableItem(shortZammadTicket),
      );
    }
    return _detailedJobTicketsTableData;
  });

  const ability = useAbility();
  const isStaff = computed(() => ability.can('staff', 'Job'));

  const detailedJobQueriesTableData = computed(() => {
    const _detailedJobQueriesTableData: DetailedJobQueriesTableDataItem[] = [];
    if (!data.value.detailedJob?.messages.length) {
      return _detailedJobQueriesTableData;
    }
    for (const message of data.value.detailedJob.messages) {
      if (!message.is_private || isStaff.value) {
        _detailedJobQueriesTableData.push(
          detailedJobQueriesTableHelpers.getTableItem(message),
        );
      }
    }
    return _detailedJobQueriesTableData;
  });

  const detailedJobAirRoadJobsTableData = computed(() => {
    const _detailedJobAirRoadJobsTableData: DetailedJobAirRoadJobsTableDataItem[] =
      [];
    if (!data.value.airroadJobs?.length) {
      return _detailedJobAirRoadJobsTableData;
    }
    for (const arJob of data.value.airroadJobs) {
      _detailedJobAirRoadJobsTableData.push(
        detailedJobAirRoadJobsTableHelpers.getTableItem(arJob),
      );
    }
    return _detailedJobAirRoadJobsTableData;
  });

  // TODO maybe place it to the state?
  const _uploadedAttachments = ref<openapi.JobDetailedOpenapi['attachments']>();
  const updatedAttachments = computed(() => {
    return _uploadedAttachments.value || data.value.detailedJob?.attachments;
  });

  const initialData = computed<DetailedJobAttachmentsFormInitialData>(() => {
    return {
      attachments: undefined,
    };
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defDetailedJobPageViewState));
    _detailedJobPageViewStore.$reset();
  }

  function init(
    viewData: DetailedJobPageViewDataComposable,
    routeQuery: CommonRouteQueryComposable,
  ) {
    _detailedJobPageViewStore.setData(viewData);
    _detailedJobPageViewStore.setQuery(routeQuery);
  }

  function handleProductsTableToggleMoreClick(stopNumberKey: string): void {
    state.productsTableShowMore[stopNumberKey] =
      !state.productsTableShowMore[stopNumberKey];
  }

  function handleSubmit(
    formData: DetailedJobAttachmentsFormValidData,
    cb: () => void,
  ): void {
    if (!data.value.detailedJob) return;
    const jobId = data.value.detailedJob.rockhopper_id;
    const jobDate = data.value.detailedJob.rockhopper_date;
    if (!formData.attachments?.length) return;
    if (state.isFetchingFormData) return;
    state.isFetchingFormData = true;
    void fetchPostJobAttachment({
      rh_id: jobId,
      rh_date: jobDate,
      files: formData.attachments,
    })
      .then((_data) => {
        if (!_data.saved?.length) return;
        _uploadedAttachments.value = [
          ..._data.saved,
          ...(data.value.detailedJob?.attachments || []),
        ];
        cb();
      })
      .finally(() => {
        state.isFetchingFormData = false;
      });
  }

  async function handleDownloadShippingLabelClick(): Promise<void> {
    if (!data.value.detailedJob) return;
    const jobId = data.value.detailedJob.rockhopper_id;
    const jobDate = data.value.detailedJob.rockhopper_date;
    if (state.isDownloading) return;
    state.isDownloading = true;
    await fetchGetJobPrintoutFile({
      job_id: jobId,
      job_date: jobDate,
    }).finally(() => {
      state.isDownloading = false;
    });
  }

  watch(
    () => data.value.detailedJob?.attachments,
    (newValue, oldValue) => {
      _uploadedAttachments.value = undefined;
    },
  );

  return {
    ...toRefs(state),
    data,
    query,
    isFetchingAllData,
    tablesDataLengths,
    productsTablesData,
    detailedJobTicketsTableData,
    detailedJobQueriesTableData,
    detailedJobAirRoadJobsTableData,
    updatedAttachments,
    initialData,
    $reset,
    init,
    handleProductsTableToggleMoreClick,
    handleSubmit,
    handleDownloadShippingLabelClick,
  };
}

const usePrivateDetailedJobPageViewStore = defineStore(
  '_detailedJobPageViewStore',
  privateDetailedJobPageViewStoreSetup,
);

export const useDetailedJobPageViewStore = defineStore(
  'detailedJobPageViewStore',
  detailedJobPageViewStoreSetup,
);
